import { RightOutlined } from '@ant-design/icons';
import Logo from 'components/navigation/Logo';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Carousel, CarouselApi, CarouselContent, CarouselDots, CarouselItem } from 'components/ui/carousel';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { routes } from 'services/RouteService';
import { selectCurrentUser } from 'store/users/selectors';

const SPECIALTY_TAGS_MEDICAL = [
  { emoji: '🌸', key: 'gynecologists' },
  { emoji: '🌿', key: 'midwives' },
  { emoji: '🤲', key: 'physiotherapists' },
  { emoji: '🥑', key: 'nutritionists' },
  { emoji: '💬', key: 'psychologists' },
  { emoji: '👶', key: 'childcareNurses' },
  { emoji: '🦴', key: 'osteopaths' },
  { emoji: '👩‍⚕️', key: 'nurses' },
];

const SPECIALTY_TAGS_SOFT = [
  { emoji: '🌱', key: 'naturopaths' },
  { emoji: '🧘', key: 'sophrologists' },
];

// Specialty pills (page 1): white, bordered, body-sized text — not a status badge.
// Clear hierarchy — bold dark title + discrete subtitle.
// No green inline emphasis (previously confused with clickable text).
const specialtyGroupLabelClassName =
  'mb-2.5 text-center text-2xs font-bold tracking-widest text-subtle-foreground uppercase';

export default function OnboardingV2Page() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useSelector(selectCurrentUser);

  const [carousel, setCarousel] = useState<CarouselApi>();

  const nextPage = (isLastPage: boolean) => {
    if (isLastPage) {
      navigate(routes.home.route);
    } else {
      carousel?.scrollNext();
    }
  };

  const teamPage = (
    <>
      <div className="mb-5">
        <p className={specialtyGroupLabelClassName}>{t('onboarding.carouselV2.pages.team.groupMedical')}</p>
        <div className="flex flex-wrap justify-center gap-2">
          {SPECIALTY_TAGS_MEDICAL.map(tag => (
            <Badge key={tag.key} variant="outline">
              {tag.emoji} {t(`onboarding.carouselV2.pages.team.specialties.${tag.key}`)}
            </Badge>
          ))}
        </div>
      </div>
      <div className="mb-5">
        <p className={specialtyGroupLabelClassName}>{t('onboarding.carouselV2.pages.team.groupSoft')}</p>
        <div className="flex flex-wrap justify-center gap-2">
          {SPECIALTY_TAGS_SOFT.map(tag => (
            <Badge key={tag.key} variant="outline">
              {tag.emoji} {t(`onboarding.carouselV2.pages.team.specialties.${tag.key}`)}
            </Badge>
          ))}
        </div>
      </div>
    </>
  );

  const illustration = (src: string) => (
    <div className="flex justify-center">
      <img alt="" src={src} className="w-4/5 max-w-xs" />
    </div>
  );

  const firstName = currentUser?.first_name;

  const pages: {
    title: string;
    subtitle: string;
    buttonKey?: string;
    content?: React.ReactNode;
  }[] = [
    {
      title: firstName
        ? t('onboarding.carouselV2.pages.welcome.title', { firstName })
        : t('onboarding.carouselV2.pages.welcome.titleNoName'),
      subtitle: t('onboarding.carouselV2.pages.welcome.subtitle'),
      buttonKey: 'onboarding.carouselV2.buttons.discover',
    },
    {
      title: t('onboarding.carouselV2.pages.response.title'),
      subtitle: t('onboarding.carouselV2.pages.response.subtitle'),
      content: illustration(require('../assets/onboarding-chat.png')),
    },
    {
      title: t('onboarding.carouselV2.pages.team.title'),
      subtitle: t('onboarding.carouselV2.pages.team.subtitle'),
      content: teamPage,
    },
    {
      title: t('onboarding.carouselV2.pages.tracking.title'),
      subtitle: t('onboarding.carouselV2.pages.tracking.subtitle'),
      content: illustration(require('../assets/onboarding-tracking.png')),
    },
    {
      title: t('onboarding.carouselV2.pages.content.title'),
      subtitle: t('onboarding.carouselV2.pages.content.subtitle'),
      content: illustration(require('../assets/onboarding-blog-posts.png')),
    },
  ];

  return (
    <Carousel
      setApi={setCarousel}
      opts={{ loop: false }}
      className="bg-background bg-[linear-gradient(0deg,var(--background)_0%,var(--card)_100%)] bg-[size:auto_120px] bg-top bg-no-repeat"
    >
      <CarouselContent className="ml-0">
        {pages.map((page, index) => {
          const isLastPage = index === pages.length - 1;
          return (
            <CarouselItem key={index} className="pl-0">
              <div className="flex h-app-screen flex-col">
                <div className="flex flex-none justify-center pt-16 pb-1">
                  <Logo size="large" />
                </div>
                {/* Scrollable area; the group inside is vertically centered via `margin: auto`
                    (which still allows scrolling when the content is taller than the viewport). */}
                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 py-2">
                  <div className="my-auto flex w-full flex-col items-center text-center">
                    <h3>{page.title}</h3>
                    <p className="mt-2.5 mb-0 text-sm leading-normal text-subtle-foreground">{page.subtitle}</p>
                    {page.content && <div className="mt-7 w-full">{page.content}</div>}
                  </div>
                </div>
                <div className="flex flex-none justify-center px-6 pt-4 pb-12">
                  <Button size="lg" onClick={() => nextPage(isLastPage)}>
                    <RightOutlined />
                    {page.buttonKey
                      ? t(page.buttonKey)
                      : isLastPage
                        ? t('onboarding.carouselV2.buttons.end')
                        : t('onboarding.carouselV2.buttons.next')}
                  </Button>
                </div>
              </div>
            </CarouselItem>
          );
        })}
      </CarouselContent>
      <CarouselDots className="absolute inset-x-0 bottom-5 [&>button]:bg-subtle-foreground [&>button[data-active=true]]:bg-inverse" />
    </Carousel>
  );
}
